import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { SeverityLevel } from '@sentry/angular';
import { ErrorService } from './error.service';
import { ErrorMessage } from '../models/error-message.model';
import { PersonDto } from 'src/app/api/models';
import { Store } from '@ngrx/store';
import { getCurrentUser } from 'src/app/store/selectors/user.selector';
import { AppState } from 'src/app/store/states/app.state';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandlerService implements ErrorHandler {
  static readonly messageLevels: SeverityLevel;

  private store?: Store<AppState>;
  private currentUser: PersonDto | null = null;

  // Collection of urls that we don't want to log into sentry
  private skipHosts = ['localhost']
  private currentHost = '';

  constructor(
    private errorService: ErrorService,
    private injector: Injector,
  ) {
    this.currentHost = window.location.hostname || '';
    this.initializeSentry();
  }

  private async initializeSentry() {
    const response = await fetch('/assets/version.json');
    const versionInfo = await response.json();
    const version = versionInfo.version || 'dev';

    Sentry.init({
      dsn: 'https://fe9380fd254e4dae94421866aa8cf52f@o1272399.ingest.sentry.io/6465969',
      debug: false,
      environment: this.currentHost,
      release: `diana-cloud@${version}`
    });
  }

  /** 
   * Log unhandled exception at client side.
   */
  handleError(error: Error | HttpErrorResponse) {
    if (!this.currentUser) {
      this.store = this.store ?? this.injector.get<Store<AppState>>(Store);
      this.store.select(getCurrentUser).subscribe(user => this.currentUser = user);
    }

    let errorObj: ErrorMessage;
    if (error instanceof HttpErrorResponse) {
      // SERVER ERROR - We should not log server errors from the client
      errorObj = this.errorService.getServerMessage(error);
    } else {
      // CLIENT ERROR
      errorObj = this.errorService.getClientMessage(error);
    }

    if (!this.skipHosts.includes(this.currentHost) && !(error instanceof HttpErrorResponse)) {
      const email = this.currentUser?.email ?? 'N/A';
      const username = `${this.currentUser?.firstName ?? ''} ${this.currentUser?.lastName ?? ''}`;
      Sentry.withScope((scope) => {
        scope.setUser({ username, email });
        scope.setLevel('error');
        scope.setTags({
          userEmail: email
        });
        scope.setExtra('stack', error.stack);
        scope.setExtra('message', errorObj.message);
        Sentry.captureException(error);
      });
    } else {
      console.error(errorObj);
    }
  }

  /**
   * Logs messages explicitly into Sentry. Tipycally, these messages are created by devs in the code explicitly.
   * @param message The message to log.
   * @param level The severity level: "debug" | "fatal" | "error" | "warning" | "log" | "info" (Default Warning)
   * @param tags Extra Tags in the key:value form. Example: tags: { userName: "Fritz" }
   */
  static logMessage(
    message: string,
    level: SeverityLevel = 'warning',
    tags?: { [key: string]: string }
  ) {
    Sentry.withScope((scope: Sentry.Scope) => {
      scope.clear();
      if (tags) {
        scope.setTags(tags);
      }

      scope.setLevel(level);
      Sentry.captureMessage(message);
    });
  }
}